// ═══════════════════════════════════════════════════════════════
// MEITHO EMPLOYEE MODULE — Complete Professional Identity Platform
// ═══════════════════════════════════════════════════════════════

import React, {useState, useEffect, useContext, createContext} from "react";
import { initializeApp, getApps } from "firebase/app";
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc, collection, query, where, getDocs, updateDoc, addDoc, orderBy } from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyBAwGsTIxPxh-s4UwqJiIXge8Dc-zt6ivI",
  authDomain: "meitho.firebaseapp.com",
  projectId: "meitho",
  storageBucket: "meitho.firebasestorage.app",
  messagingSenderId: "61609983687",
  appId: "1:61609983687:web:f8bd4e22bd189d6f1e48e2"
};

const empApp = getApps().find(a=>a.name==="employee") || initializeApp(firebaseConfig, "employee");
const empAuth = getAuth(empApp);
const empDb = getFirestore(empApp);
// Storage available for future use

// ── Theme ──────────────────────────────────────────────────────
const ThemeCtx = createContext({dark:false,toggle:()=>{}});
const useTheme = () => useContext(ThemeCtx);

// ── Constants ──────────────────────────────────────────────────
const FF = "'Inter','Segoe UI',sans-serif";
const FFD = "'DM Sans','Inter',sans-serif";
const FFM = "'JetBrains Mono','Fira Code',monospace";

// Generate Meitho ID
const genMeithoId = () => "MIT" + Date.now().toString(36).toUpperCase().slice(-6);

// ── Meitho Score Calculator (1000 pts) ────────────────────────
function calcEmployeeScore(profile) {
  const breakdown = {identity:0, employment:0, education:0, skills:0, profileComplete:0};
  const badges = [];

  // Identity (200 pts)
  if(profile.mobile) breakdown.identity += 60;
  if(profile.aadhaar) breakdown.identity += 70;
  if(profile.pan) breakdown.identity += 50;
  if(profile.email) breakdown.identity += 20;
  breakdown.identity = Math.min(200, breakdown.identity);

  // Employment (300 pts)
  const emp = profile.employment || [];
  if(emp.length > 0) {
    const totalYears = emp.reduce((s,e) => {
      const start = new Date(e.joiningDate||"");
      const end = e.relievingDate ? new Date(e.relievingDate) : new Date();
      return s + Math.max(0,(end-start)/(1000*60*60*24*365));
    }, 0);
    const avgTenure = totalYears / emp.length;
    if(totalYears >= 5) breakdown.employment += 150;
    else if(totalYears >= 3) breakdown.employment += 100;
    else if(totalYears >= 1) breakdown.employment += 60;
    else breakdown.employment += 30;
    if(avgTenure >= 2) breakdown.employment += 100;
    else if(avgTenure >= 1) breakdown.employment += 60;
    else breakdown.employment += 20;
    if(profile.uan) breakdown.employment += 50;
    breakdown.employment = Math.min(300, breakdown.employment);
    if(totalYears >= 5) badges.push("✅ Senior Professional");
    if(avgTenure >= 3) badges.push("🏅 Loyal Employee");
  }

  // Education (200 pts)
  const edu = profile.education || [];
  if(edu.length > 0) {
    const highestDeg = edu.find(e=>e.degree?.toLowerCase().includes("b.tech")||e.degree?.toLowerCase().includes("mba")||e.degree?.toLowerCase().includes("m.tech"));
    if(highestDeg) breakdown.education += 120;
    else breakdown.education += 80;
    if(edu.length > 1) breakdown.education += 40;
    if(profile.verified?.education) breakdown.education += 40;
    breakdown.education = Math.min(200, breakdown.education);
  }

  // Skills/Certifications (150 pts)
  const certs = profile.certifications || [];
  const skills = profile.skills || [];
  breakdown.skills = Math.min(150, (certs.length * 30) + (skills.length * 10));
  if(certs.length >= 3) badges.push("🎓 Multi-Certified");

  // Profile Completeness (150 pts)
  const fields = [profile.name, profile.mobile, profile.email, profile.city, profile.dob, profile.gender, profile.designation, profile.photo];
  const filled = fields.filter(Boolean).length;
  breakdown.profileComplete = Math.round((filled/fields.length)*150);
  if(filled === fields.length) badges.push("💯 Complete Profile");

  const score = breakdown.identity + breakdown.employment + breakdown.education + breakdown.skills + breakdown.profileComplete;
  const grade = score >= 850?"Elite":score >= 700?"Excellent":score >= 550?"Good":score >= 400?"Fair":"Building";
  const color = score >= 850?"#10b981":score >= 700?"#0cc0df":score >= 550?"#8b5cf6":score >= 400?"#ff914d":"#ef4444";

  return {score, breakdown, grade, color, badges};
}

// ── UI Components ──────────────────────────────────────────────
const Spin = () => (
  <div style={{width:18,height:18,border:"2px solid rgba(255,255,255,0.3)",borderTop:"2px solid #fff",borderRadius:"50%",animation:"spin 0.8s linear infinite",display:"inline-block"}}/>
);

const Btn = ({onClick,children,style={},disabled=false}) => (
  <button onClick={onClick} disabled={disabled} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"11px 20px",cursor:disabled?"not-allowed":"pointer",fontSize:13,fontWeight:700,fontFamily:FF,display:"flex",alignItems:"center",gap:6,opacity:disabled?0.6:1,...style}}>
    {children}
  </button>
);

const BtnGhost = ({onClick,children,style={}}) => (
  <button onClick={onClick} style={{background:"transparent",border:"1px solid var(--b1,rgba(255,255,255,0.12))",borderRadius:10,color:"var(--text,#fff)",padding:"10px 18px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:FF,...style}}>
    {children}
  </button>
);

const Inp = ({value,onChange,placeholder,type="text",style={},onKeyDown}) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} onKeyDown={onKeyDown}
    style={{width:"100%",padding:"11px 14px",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,color:"#fff",fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box",...style}}/>
);

const Label = ({children}) => (
  <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)",marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>{children}</div>
);

// ── Score Ring ─────────────────────────────────────────────────
function ScoreRing({score, max=1000, color="#0cc0df", size=120}) {
  const r = size/2 - 10;
  const circ = 2*Math.PI*r;
  const pct = (score||0)/max;
  return (
    <div style={{position:"relative",width:size,height:size}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={10}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={10}
          strokeDasharray={`${pct*circ} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{transition:"stroke-dasharray 1s ease"}}/>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
        <div style={{fontFamily:FFD,fontSize:size/5,fontWeight:900,color,lineHeight:1}}>{score||0}</div>
        <div style={{fontSize:size/12,color:"rgba(255,255,255,0.4)",fontWeight:600}}>/{max}</div>
      </div>
    </div>
  );
}

// ── QR Code Generator (simple SVG) ────────────────────────────
function QRCode({value, size=120}) {
  // Simple visual QR placeholder with actual URL
  return (
    <div style={{width:size,height:size,background:"#fff",borderRadius:8,padding:6,display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>
      <div style={{position:"absolute",inset:6,display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1}}>
        {Array(49).fill(0).map((_,i)=>{
          const r=Math.floor(i/7), c=i%7;
          const corner=(r<3&&c<3)||(r<3&&c>3)||(r>3&&c<3);
          const edge=(r===0||r===6||c===0||c===6)&&corner;
          const inner=(r>=1&&r<=2&&c>=1&&c<=2)||(r>=1&&r<=2&&c>=4&&c<=5)||(r>=4&&r<=5&&c>=1&&c<=2);
          const data=!corner&&(value.charCodeAt(i%value.length)+i)%3!==0;
          const fill=edge||inner||data?"#000":"transparent";
          return <div key={i} style={{background:fill,borderRadius:1}}/>;
        })}
      </div>
    </div>
  );
}

// ── Digital Card ───────────────────────────────────────────────
function DigitalCard({profile, score, onDownload}) {
  const ms = calcEmployeeScore(profile);
  return (
    <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e,#0a1628)",borderRadius:20,padding:24,width:"100%",maxWidth:360,boxShadow:"0 20px 60px rgba(0,0,0,0.5)",border:"1px solid rgba(12,192,223,0.3)",position:"relative",overflow:"hidden",fontFamily:FF}}>
      {/* Background pattern */}
      <div style={{position:"absolute",inset:0,opacity:0.05,background:"radial-gradient(circle at 80% 20%, #0cc0df 0%, transparent 50%), radial-gradient(circle at 20% 80%, #ff3131 0%, transparent 50%)"}}/>
      
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,position:"relative"}}>
        <div>
          <div style={{fontSize:10,color:"#0cc0df",fontWeight:700,letterSpacing:2,marginBottom:4}}>MEITHO BGV</div>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",letterSpacing:1}}>VERIFIED PROFESSIONAL</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:9,color:"rgba(255,255,255,0.4)"}}>MEITHO ID</div>
          <div style={{fontSize:12,fontWeight:700,color:"#0cc0df",fontFamily:FFM}}>{profile.meithoId||"MIT000000"}</div>
        </div>
      </div>

      {/* Profile section */}
      <div style={{display:"flex",gap:14,alignItems:"center",marginBottom:20,position:"relative"}}>
        <div style={{width:64,height:64,borderRadius:"50%",background:profile.photo?"transparent":"linear-gradient(135deg,#ff3131,#ff914d)",border:"2px solid #0cc0df",overflow:"hidden",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,fontWeight:700,color:"#fff"}}>
          {profile.photo?<img src={profile.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:(profile.name||"?")[0]?.toUpperCase()}
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:18,fontWeight:800,color:"#fff",marginBottom:2}}>{profile.name||"Your Name"}</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>{profile.designation||"Professional"}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{profile.currentCompany||""} {profile.city?`· ${profile.city}`:""}</div>
        </div>
      </div>

      {/* Score bar */}
      <div style={{background:"rgba(255,255,255,0.05)",borderRadius:12,padding:"12px 16px",marginBottom:16,border:"1px solid rgba(255,255,255,0.08)",position:"relative"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:9,color:"rgba(255,255,255,0.4)",marginBottom:4,letterSpacing:1}}>MEITHO SCORE</div>
            <div style={{display:"flex",alignItems:"baseline",gap:4}}>
              <div style={{fontSize:32,fontWeight:900,color:ms.color,fontFamily:FFD}}>{ms.score}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.3)"}}>/1000</div>
            </div>
            <div style={{fontSize:11,fontWeight:700,color:ms.color}}>{ms.grade}</div>
          </div>
          <QRCode value={`https://meitho.com/p/${profile.meithoId||"demo"}`} size={70}/>
        </div>
        <div style={{marginTop:10,height:4,background:"rgba(255,255,255,0.08)",borderRadius:2,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${(ms.score/1000)*100}%`,background:ms.color,borderRadius:2}}/>
        </div>
      </div>

      {/* Verification badges */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16,position:"relative"}}>
        {[
          {label:"Identity",done:!!(profile.aadhaar&&profile.pan)},
          {label:"Employment",done:!!(profile.employment?.length>0)},
          {label:"Education",done:!!(profile.education?.length>0)},
        ].map(b=>(
          <div key={b.label} style={{fontSize:10,fontWeight:600,padding:"3px 8px",borderRadius:99,background:b.done?"rgba(16,185,129,0.15)":"rgba(255,255,255,0.05)",border:`1px solid ${b.done?"#10b981":"rgba(255,255,255,0.1)"}`,color:b.done?"#10b981":"rgba(255,255,255,0.3)"}}>
            {b.done?"✓":"○"} {b.label}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:12,position:"relative"}}>
        <div style={{fontSize:9,color:"rgba(255,255,255,0.3)"}}>meitho.com/p/{profile.meithoId||"demo"}</div>
        <div style={{fontSize:9,color:"rgba(255,255,255,0.3)"}}>Valid 2026</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCREENS
// ═══════════════════════════════════════════════════════════════

// ── Splash ────────────────────────────────────────────────────
function SplashScreen() {
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:FF}}>
      <div style={{width:80,height:80,borderRadius:20,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20,boxShadow:"0 20px 40px rgba(255,49,49,0.4)"}}>
        <svg width="40" height="40" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/><path d="M7.5 10l2 2 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
      <div style={{fontFamily:FFD,fontSize:32,fontWeight:900,color:"#fff",marginBottom:8}}>Meitho</div>
      <div style={{fontSize:14,color:"rgba(255,255,255,0.5)",marginBottom:40}}>Your Verified Professional Identity</div>
      <div style={{width:40,height:40,border:"3px solid rgba(255,255,255,0.1)",borderTop:"3px solid #ff3131",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────
function LoginScreen({onLogin, onRegister}) {
  const [mobile,setMobile]=useState("");
  const [password,setPassword]=useState("");
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");

  const doLogin=async()=>{
    if(!mobile||!password){setErr("Enter email and password");return;}
    setLoading(true);setErr("");
    try{
      let emailToUse = mobile.trim();

      // If mobile number entered, look up email from Firestore
      if(!emailToUse.includes("@")){
        try{
          const snap=await getDocs(query(collection(empDb,"employeeProfiles"),where("mobile","==",emailToUse)));
          if(!snap.empty){
            const profile=snap.docs[0].data();
            // Use authEmail if stored, else actual email
            emailToUse = profile.authEmail || profile.email;
          } else {
            setErr("No account found with this mobile number.");
            setLoading(false);return;
          }
        }catch(e){setErr("Login failed. Try with your email address.");setLoading(false);return;}
      }

      // Try login with email
      try {
        await signInWithEmailAndPassword(empAuth, emailToUse, password);
      } catch(e1) {
        // If failed, try emp-prefixed version
        if(e1.code==="auth/invalid-credential"||e1.code==="auth/user-not-found"){
          try{
            const empEmail=emailToUse.includes("@emp.")?emailToUse:emailToUse.replace("@","@emp.");
            await signInWithEmailAndPassword(empAuth, empEmail, password);
          }catch(e2){
            setErr("Invalid email or password. Please try again.");
            setLoading(false);return;
          }
        } else {
          throw e1;
        }
      }
    }catch(e){
      setErr(e.code==="auth/too-many-requests"?"Too many attempts. Try later.":"Login failed. Check your credentials.");
    }
    setLoading(false);
  };

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",display:"flex",flexDirection:"column",fontFamily:FF}}>
      {/* Header */}
      <div style={{padding:"60px 32px 40px",textAlign:"center"}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:12,marginBottom:16}}>
          <div style={{width:44,height:44,borderRadius:12,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
          </div>
          <div style={{fontFamily:FFD,fontSize:24,fontWeight:900,color:"#fff"}}>Meitho</div>
        </div>
        <div style={{fontSize:22,fontWeight:700,color:"#fff",marginBottom:6}}>Welcome Back</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.5)"}}>Sign in to your professional profile</div>
      </div>

      {/* Form */}
      <div style={{flex:1,padding:"0 24px",maxWidth:400,margin:"0 auto",width:"100%",boxSizing:"border-box"}}>
        <div style={{marginBottom:16}}>
          <Label>Email Address or Mobile Number</Label>
          <Inp value={mobile} onChange={e=>setMobile(e.target.value)} placeholder="email@domain.com or 9876543210" onKeyDown={e=>e.key==="Enter"&&doLogin()}/>
        </div>
        <div style={{marginBottom:8}}>
          <Label>Password</Label>
          <Inp type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Enter password" onKeyDown={e=>e.key==="Enter"&&doLogin()}/>
        </div>
        <div style={{textAlign:"right",marginBottom:20}}>
          <button style={{background:"none",border:"none",color:"#0cc0df",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>Forgot Password?</button>
        </div>
        {err&&<div style={{background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#ef4444",marginBottom:14}}>{err}</div>}
        <Btn onClick={doLogin} style={{width:"100%",justifyContent:"center",marginBottom:16}}>
          {loading?<Spin/>:"Sign In →"}
        </Btn>
        <div style={{textAlign:"center",fontSize:13,color:"rgba(255,255,255,0.5)"}}>
          New to Meitho?{" "}
          <button onClick={onRegister} style={{background:"none",border:"none",color:"#ff914d",cursor:"pointer",fontWeight:700,fontFamily:FF,fontSize:13}}>Create Profile →</button>
        </div>
      </div>

      {/* Bottom */}
      <div style={{padding:"24px",textAlign:"center",fontSize:11,color:"rgba(255,255,255,0.2)"}}>
        Employee Portal · meitho.com · By Meitho BGV
      </div>
    </div>
  );
}

// ── Registration ──────────────────────────────────────────────
function RegisterScreen({onLogin}) {
  const [step,setStep]=useState(1);
  const [form,setForm]=useState({
    name:"",mobile:"",email:"",password:"",confirm:"",
    aadhaar:"",pan:"",uan:"",
    designation:"",company:"",city:"",
    terms:false
  });
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");

  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  const doRegister=async()=>{
    if(!form.name||!form.mobile||!form.email||!form.password){setErr("Fill all required fields");return;}
    if(!form.aadhaar||!form.pan){setErr("Aadhaar and PAN are mandatory");return;}
    if(form.password!==form.confirm){setErr("Passwords don't match");return;}
    if(!form.terms){setErr("Please accept terms & conditions");return;}
    setLoading(true);setErr("");
    try{
      const emailToUse = form.email;
      let cred;
      try {
        cred = await createUserWithEmailAndPassword(empAuth, emailToUse, form.password);
      } catch(authErr) {
        if(authErr.code === "auth/email-already-in-use") {
          // Email used by company account - use emp prefix
          const empEmail = form.email.replace("@","@emp.");
          cred = await createUserWithEmailAndPassword(empAuth, empEmail, form.password);
        } else throw authErr;
      }
      const authEmail = cred.user.email; // actual email used in Firebase Auth
      const meithoId = genMeithoId();
      const profileData = {
        uid: cred.user.uid,
        meithoId,
        name: form.name,
        mobile: form.mobile,
        email: form.email,  // Always store actual email
        authEmail: authEmail, // Store what Firebase Auth used
        aadhaar: form.aadhaar,
        pan: form.pan.toUpperCase(),
        uan: form.uan || "",
        designation: form.designation || "",
        currentCompany: form.company || "",
        city: form.city || "",
        createdAt: new Date().toISOString(),
        employment: [],
        education: [],
        skills: [],
        certifications: [],
        documents: {},
        verified: {},
        verificationStatus: "pending",
        paymentStatus: "unpaid",
      };
      await setDoc(doc(empDb,"employeeProfiles",cred.user.uid), profileData);
      // Also save by mobile and PAN for search
      await setDoc(doc(empDb,"employeeLookup",form.mobile), {uid:cred.user.uid, meithoId});
      await setDoc(doc(empDb,"employeeLookup",form.pan.toUpperCase()), {uid:cred.user.uid, meithoId});
      if(form.aadhaar) await setDoc(doc(empDb,"employeeLookup",form.aadhaar.replace(/\D/g,"")), {uid:cred.user.uid, meithoId});
    }catch(e){
      if(e.code==="auth/email-already-in-use") setErr("Email already registered. Please sign in.");
      else setErr(e.message);
    }
    setLoading(false);
  };

  const StepBar=()=>(
    <div style={{display:"flex",alignItems:"center",marginBottom:24,padding:"0 4px"}}>
      {[{n:1,l:"Basic"},{n:2,l:"Identity"},{n:3,l:"Work"}].map((s,i)=>(
        <div key={s.n} style={{display:"flex",alignItems:"center",flex:1}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",flex:1}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:step>=s.n?"linear-gradient(135deg,#ff3131,#ff914d)":"rgba(255,255,255,0.06)",border:`2px solid ${step>=s.n?"#ff3131":"rgba(255,255,255,0.1)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:step>=s.n?"#fff":"rgba(255,255,255,0.3)"}}>
              {step>s.n?"✓":s.n}
            </div>
            <div style={{fontSize:9,color:step>=s.n?"rgba(255,255,255,0.7)":"rgba(255,255,255,0.3)",fontWeight:600,marginTop:3}}>{s.l}</div>
          </div>
          {i<2&&<div style={{height:2,width:30,background:step>s.n?"#ff3131":"rgba(255,255,255,0.1)",marginBottom:14,flexShrink:0}}/>}
        </div>
      ))}
    </div>
  );

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",fontFamily:FF}}>
      <div style={{padding:"40px 24px 24px",maxWidth:420,margin:"0 auto"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:28}}>
          <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
          </div>
          <div>
            <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:"#fff"}}>Create Profile</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>Step {step} of 3</div>
          </div>
        </div>

        <StepBar/>

        {/* Step 1 - Basic */}
        {step===1&&(
          <div>
            <div style={{fontSize:11,color:"#0cc0df",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Personal Information</div>
            {[
              {k:"name",l:"Full Name *",p:"As per Aadhaar"},
              {k:"mobile",l:"Mobile Number *",p:"10-digit mobile"},
              {k:"email",l:"Email Address *",p:"you@email.com"},
              {k:"password",l:"Password *",p:"Min 8 characters",t:"password"},
              {k:"confirm",l:"Confirm Password *",p:"Re-enter password",t:"password"},
            ].map(fi=>(
              <div key={fi.k} style={{marginBottom:12}}>
                <Label>{fi.l}</Label>
                <Inp type={fi.t||"text"} value={form[fi.k]} onChange={e=>f(fi.k,e.target.value)} placeholder={fi.p}/>
              </div>
            ))}
            {err&&<div style={{color:"#ef4444",fontSize:12,marginBottom:12}}>{err}</div>}
            <Btn onClick={()=>{
              if(!form.name||!form.mobile||!form.email||!form.password){setErr("Fill all required fields");return;}
              if(form.password!==form.confirm){setErr("Passwords don't match");return;}
              setErr("");setStep(2);
            }} style={{width:"100%",justifyContent:"center"}}>Next → Identity</Btn>
            <div style={{textAlign:"center",marginTop:14,fontSize:12,color:"rgba(255,255,255,0.4)"}}>
              Already registered?{" "}
              <button onClick={onLogin} style={{background:"none",border:"none",color:"#ff914d",cursor:"pointer",fontWeight:700,fontFamily:FF,fontSize:12}}>Sign In</button>
            </div>
          </div>
        )}

        {/* Step 2 - Identity */}
        {step===2&&(
          <div>
            <div style={{fontSize:11,color:"#0cc0df",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Identity Documents</div>
            <div style={{background:"rgba(12,192,223,0.08)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"rgba(255,255,255,0.6)",marginBottom:16,lineHeight:1.6}}>
              🔒 Your data is encrypted and shared only with your consent. Aadhaar & PAN are required for verification.
            </div>
            {[
              {k:"aadhaar",l:"Aadhaar Number *",p:"XXXX XXXX XXXX"},
              {k:"pan",l:"PAN Number *",p:"ABCDE1234F"},
              {k:"uan",l:"UAN Number (Optional)",p:"12-digit EPFO UAN"},
            ].map(fi=>(
              <div key={fi.k} style={{marginBottom:12}}>
                <Label>{fi.l}</Label>
                <Inp value={form[fi.k]} onChange={e=>f(fi.k,fi.k==="pan"?e.target.value.toUpperCase():e.target.value)} placeholder={fi.p} style={{fontFamily:FFM}}/>
              </div>
            ))}
            {err&&<div style={{color:"#ef4444",fontSize:12,marginBottom:12}}>{err}</div>}
            <div style={{display:"flex",gap:8}}>
              <BtnGhost onClick={()=>{setErr("");setStep(1);}}>← Back</BtnGhost>
              <Btn onClick={()=>{
                if(!form.aadhaar||!form.pan){setErr("Aadhaar and PAN are mandatory");return;}
                setErr("");setStep(3);
              }} style={{flex:1,justifyContent:"center"}}>Next → Work</Btn>
            </div>
          </div>
        )}

        {/* Step 3 - Work */}
        {step===3&&(
          <div>
            <div style={{fontSize:11,color:"#0cc0df",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Professional Details (Optional)</div>
            {[
              {k:"designation",l:"Current Designation",p:"Software Engineer"},
              {k:"company",l:"Current Company",p:"Acme Corp"},
              {k:"city",l:"City",p:"Chennai"},
            ].map(fi=>(
              <div key={fi.k} style={{marginBottom:12}}>
                <Label>{fi.l}</Label>
                <Inp value={form[fi.k]} onChange={e=>f(fi.k,e.target.value)} placeholder={fi.p}/>
              </div>
            ))}
            {/* Terms */}
            <div onClick={()=>f("terms",!form.terms)} style={{display:"flex",gap:10,alignItems:"flex-start",cursor:"pointer",padding:"12px",background:"rgba(255,255,255,0.03)",border:`1px solid ${form.terms?"rgba(16,185,129,0.3)":"rgba(255,255,255,0.08)"}`,borderRadius:8,marginBottom:16,userSelect:"none"}}>
              <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${form.terms?"#10b981":"rgba(255,255,255,0.2)"}`,background:form.terms?"#10b981":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                {form.terms&&<svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>}
              </div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.5}}>
                I agree to Meitho's <span style={{color:"#0cc0df"}}>Terms of Service</span> and <span style={{color:"#0cc0df"}}>Privacy Policy</span>. I consent to verification of my provided information.
              </div>
            </div>
            {err&&<div style={{color:"#ef4444",fontSize:12,marginBottom:12}}>{err}</div>}
            <div style={{display:"flex",gap:8}}>
              <BtnGhost onClick={()=>{setErr("");setStep(2);}}>← Back</BtnGhost>
              <Btn onClick={doRegister} style={{flex:1,justifyContent:"center"}}>{loading?<Spin/>:"Create Profile 🎉"}</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────
function DashboardScreen({profile, setScreen}) {
  const ms = calcEmployeeScore(profile);
  const completion = Math.round((
    [profile.name,profile.mobile,profile.email,profile.aadhaar,profile.pan,profile.designation,profile.city,profile.photo].filter(Boolean).length / 8
  ) * 100);

  const quickActions = [
    {icon:"👤",label:"Edit Profile",screen:"profile"},
    {icon:"🎓",label:"Add Education",screen:"education"},
    {icon:"💼",label:"Add Employment",screen:"employment"},
    {icon:"📄",label:"Documents",screen:"documents"},
    {icon:"✅",label:"Get Verified",screen:"verification"},
    {icon:"🏆",label:"My Card",screen:"card"},
  ];

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(180deg,#0a0a1a 0%,#0d0d1f 100%)",fontFamily:FF,paddingBottom:80}}>
      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",padding:"48px 20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:4}}>Welcome back,</div>
            <div style={{fontFamily:FFD,fontSize:20,fontWeight:800,color:"#fff"}}>{profile.name?.split(" ")[0]||"Professional"} 👋</div>
          </div>
          <div style={{width:44,height:44,borderRadius:"50%",background:profile.photo?"transparent":"linear-gradient(135deg,#ff3131,#ff914d)",border:"2px solid rgba(12,192,223,0.4)",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,color:"#fff",cursor:"pointer"}} onClick={()=>setScreen("profile")}>
            {profile.photo?<img src={profile.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : (profile.name||"?")[0]?.toUpperCase()}
          </div>
        </div>

        {/* Score card */}
        <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:16,padding:20,display:"flex",alignItems:"center",gap:20}}>
          <ScoreRing score={ms.score} color={ms.color} size={90}/>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",letterSpacing:1,marginBottom:4}}>MEITHO SCORE</div>
            <div style={{fontSize:20,fontWeight:800,color:ms.color,marginBottom:2}}>{ms.grade}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:10}}>ID: {profile.meithoId}</div>
            {/* Mini breakdown */}
            {Object.entries({Identity:ms.breakdown.identity,Employment:ms.breakdown.employment,Education:ms.breakdown.education}).map(([l,v])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <div style={{fontSize:9,color:"rgba(255,255,255,0.3)",width:60}}>{l}</div>
                <div style={{flex:1,height:3,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${(v/300)*100}%`,background:ms.color,borderRadius:2}}/>
                </div>
                <div style={{fontSize:9,color:ms.color,width:24,textAlign:"right"}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Profile completion */}
      {completion < 100&&(
        <div style={{margin:"16px 20px",background:"rgba(255,145,77,0.08)",border:"1px solid rgba(255,145,77,0.2)",borderRadius:12,padding:"12px 16px"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:"#ff914d"}}>Complete your profile</div>
            <div style={{fontSize:12,color:"#ff914d",fontWeight:700}}>{completion}%</div>
          </div>
          <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${completion}%`,background:"linear-gradient(90deg,#ff3131,#ff914d)",borderRadius:2}}/>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{padding:"20px 20px 0"}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Quick Actions</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
          {quickActions.map(a=>(
            <button key={a.screen} onClick={()=>setScreen(a.screen)} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"14px 10px",cursor:"pointer",textAlign:"center",transition:"all .15s",fontFamily:FF}}>
              <div style={{fontSize:22,marginBottom:6}}>{a.icon}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.6)",fontWeight:600}}>{a.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Badges */}
      {ms.badges?.length>0&&(
        <div style={{padding:"20px 20px 0"}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Your Badges</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {ms.badges.map((b,i)=>(
              <div key={i} style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:99,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.7)"}}>{b}</div>
            ))}
          </div>
        </div>
      )}

      {/* Verification status */}
      <div style={{margin:"20px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:16}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:12}}>Verification Status</div>
        {[
          {label:"Identity Verification",done:!!(profile.aadhaar&&profile.pan),action:"verification"},
          {label:"Employment Verification",done:profile.verified?.employment,action:"employment"},
          {label:"Education Verification",done:profile.verified?.education,action:"education"},
          {label:"Profile Verification",done:profile.paymentStatus==="paid",action:"verification"},
        ].map((v,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <div style={{width:20,height:20,borderRadius:"50%",background:v.done?"rgba(16,185,129,0.2)":"rgba(255,255,255,0.06)",border:`1px solid ${v.done?"#10b981":"rgba(255,255,255,0.1)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:v.done?"#10b981":"rgba(255,255,255,0.3)"}}>
                {v.done?"✓":"○"}
              </div>
              <div style={{fontSize:12,color:v.done?"rgba(255,255,255,0.7)":"rgba(255,255,255,0.4)"}}>{v.label}</div>
            </div>
            {!v.done&&<button onClick={()=>setScreen(v.action)} style={{background:"none",border:"none",color:"#0cc0df",fontSize:11,cursor:"pointer",fontFamily:FF,fontWeight:600}}>Add →</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Profile Screen ────────────────────────────────────────────
function ProfileScreen({profile, setProfile, setScreen}) {
  const [editing,setEditing]=useState(false);
  const [form,setForm]=useState({...profile});
  const [saving,setSaving]=useState(false);

  const save=async()=>{
    setSaving(true);
    try{
      await updateDoc(doc(empDb,"employeeProfiles",profile.uid),{
        name:form.name,mobile:form.mobile,email:form.email,
        dob:form.dob||"",gender:form.gender||"",
        city:form.city||"",address:form.address||"",
        designation:form.designation||"",currentCompany:form.currentCompany||"",
        skills:form.skills||[],
      });
      setProfile(p=>({...p,...form}));
      setEditing(false);
    }catch(e){alert("Save failed: "+e.message);}
    setSaving(false);
  };

  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  return(
    <div style={{minHeight:"100vh",background:"#0a0a1a",fontFamily:FF,paddingBottom:80}}>
      <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",padding:"48px 20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <button onClick={()=>setScreen("dashboard")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontSize:20,padding:0}}>←</button>
          <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"#fff"}}>My Profile</div>
          <button onClick={()=>editing?save():setEditing(true)} style={{background:editing?"linear-gradient(90deg,#ff3131,#ff914d)":"rgba(255,255,255,0.08)",border:"none",borderRadius:8,color:"#fff",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>
            {saving?<Spin/>:editing?"Save":"Edit"}
          </button>
        </div>
        {/* Avatar */}
        <div style={{textAlign:"center"}}>
          <div style={{width:80,height:80,borderRadius:"50%",background:"linear-gradient(135deg,#ff3131,#ff914d)",border:"3px solid rgba(12,192,223,0.4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,fontWeight:700,color:"#fff",margin:"0 auto 12px"}}>
            {(profile.name||"?")[0]?.toUpperCase()}
          </div>
          <div style={{fontFamily:FFD,fontSize:20,fontWeight:800,color:"#fff"}}>{profile.name}</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:2}}>{profile.meithoId}</div>
        </div>
      </div>

      <div style={{padding:"20px"}}>
        {/* Personal */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,color:"#0cc0df",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Personal Information</div>
          {[
            {k:"name",l:"Full Name",p:"Your name"},
            {k:"mobile",l:"Mobile",p:"10-digit number"},
            {k:"email",l:"Email",p:"email@domain.com"},
            {k:"dob",l:"Date of Birth",p:"YYYY-MM-DD"},
            {k:"gender",l:"Gender",p:"Male / Female / Other"},
            {k:"city",l:"City",p:"Chennai"},
            {k:"address",l:"Address",p:"Full address"},
          ].map(fi=>(
            <div key={fi.k} style={{marginBottom:12,background:"rgba(255,255,255,0.03)",borderRadius:10,padding:"12px 14px",border:"1px solid rgba(255,255,255,0.06)"}}>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",fontWeight:700,textTransform:"uppercase",marginBottom:4}}>{fi.l}</div>
              {editing
                ?<Inp value={form[fi.k]||""} onChange={e=>f(fi.k,e.target.value)} placeholder={fi.p} style={{padding:"6px 0",background:"transparent",border:"none",borderBottom:"1px solid rgba(255,255,255,0.1)",borderRadius:0}}/>
                :<div style={{fontSize:14,color:"#fff",fontWeight:500}}>{profile[fi.k]||"—"}</div>
              }
            </div>
          ))}
        </div>

        {/* Identity */}
        <div>
          <div style={{fontSize:11,color:"#0cc0df",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Identity Documents</div>
          {[
            {k:"aadhaar",l:"Aadhaar Number"},
            {k:"pan",l:"PAN Number"},
            {k:"uan",l:"UAN Number"},
          ].map(fi=>(
            <div key={fi.k} style={{marginBottom:10,background:"rgba(255,255,255,0.03)",borderRadius:10,padding:"12px 14px",border:"1px solid rgba(255,255,255,0.06)"}}>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",fontWeight:700,textTransform:"uppercase",marginBottom:4}}>{fi.l}</div>
              <div style={{fontSize:13,color:"#fff",fontFamily:FFM}}>{profile[fi.k]||"—"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Education Screen ──────────────────────────────────────────
function EducationScreen({profile, setProfile, setScreen}) {
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({institution:"",degree:"",branch:"",year:"",percentage:""});
  const [saving,setSaving]=useState(false);
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  const addEdu=async()=>{
    if(!form.institution||!form.degree){alert("Institution and degree required");return;}
    setSaving(true);
    const newEdu=[...(profile.education||[]),{...form,id:Date.now().toString(),status:"unverified"}];
    await updateDoc(doc(empDb,"employeeProfiles",profile.uid),{education:newEdu});
    setProfile(p=>({...p,education:newEdu}));
    setShowAdd(false);
    setForm({institution:"",degree:"",branch:"",year:"",percentage:""});
    setSaving(false);
  };

  return(
    <div style={{minHeight:"100vh",background:"#0a0a1a",fontFamily:FF,paddingBottom:80}}>
      <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",padding:"48px 20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
          <button onClick={()=>setScreen("dashboard")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontSize:20,padding:0}}>←</button>
          <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"#fff"}}>Education</div>
          <button onClick={()=>setShowAdd(true)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>+ Add</button>
        </div>
      </div>
      <div style={{padding:"20px"}}>
        {showAdd&&(
          <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:14,padding:18,marginBottom:20}}>
            <div style={{fontSize:12,color:"#0cc0df",fontWeight:700,marginBottom:14}}>Add Education</div>
            {[
              {k:"institution",l:"Institution *",p:"IIT Madras / Anna University"},
              {k:"degree",l:"Degree *",p:"B.Tech / MBA / MCA"},
              {k:"branch",l:"Branch/Specialization",p:"Computer Science"},
              {k:"year",l:"Passing Year",p:"2020"},
              {k:"percentage",l:"Percentage/CGPA",p:"85% or 8.5 CGPA"},
            ].map(fi=>(
              <div key={fi.k} style={{marginBottom:12}}>
                <Label>{fi.l}</Label>
                <Inp value={form[fi.k]} onChange={e=>f(fi.k,e.target.value)} placeholder={fi.p}/>
              </div>
            ))}
            <div style={{display:"flex",gap:8}}>
              <BtnGhost onClick={()=>setShowAdd(false)}>Cancel</BtnGhost>
              <Btn onClick={addEdu} style={{flex:1,justifyContent:"center"}}>{saving?<Spin/>:"Save Education"}</Btn>
            </div>
          </div>
        )}
        {(profile.education||[]).length===0&&!showAdd&&(
          <div style={{textAlign:"center",padding:"40px 20px",color:"rgba(255,255,255,0.3)"}}>
            <div style={{fontSize:40,marginBottom:12}}>🎓</div>
            <div style={{fontSize:14,marginBottom:8}}>No education added yet</div>
            <button onClick={()=>setShowAdd(true)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF}}>+ Add Education</button>
          </div>
        )}
        {(profile.education||[]).map((e,i)=>(
          <div key={i} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:16,marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:"#fff",marginBottom:2}}>{e.degree} {e.branch?`in ${e.branch}`:""}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginBottom:4}}>{e.institution}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{e.year} {e.percentage?`· ${e.percentage}`:""}</div>
              </div>
              <div style={{fontSize:10,padding:"3px 8px",borderRadius:99,background:"rgba(255,145,77,0.12)",border:"1px solid rgba(255,145,77,0.3)",color:"#ff914d",fontWeight:600}}>Unverified</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Employment Screen ─────────────────────────────────────────
function EmploymentScreen({profile, setProfile, setScreen}) {
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({company:"",designation:"",joiningDate:"",relievingDate:"",type:"Full-time",isCurrent:false});
  const [saving,setSaving]=useState(false);
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  const addEmp=async()=>{
    if(!form.company||!form.designation){alert("Company and designation required");return;}
    setSaving(true);
    const newEmp=[...(profile.employment||[]),{...form,id:Date.now().toString(),status:"unverified"}];
    await updateDoc(doc(empDb,"employeeProfiles",profile.uid),{employment:newEmp});
    setProfile(p=>({...p,employment:newEmp}));
    setShowAdd(false);
    setForm({company:"",designation:"",joiningDate:"",relievingDate:"",type:"Full-time",isCurrent:false});
    setSaving(false);
  };

  return(
    <div style={{minHeight:"100vh",background:"#0a0a1a",fontFamily:FF,paddingBottom:80}}>
      <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",padding:"48px 20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <button onClick={()=>setScreen("dashboard")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontSize:20,padding:0}}>←</button>
          <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"#fff"}}>Employment</div>
          <button onClick={()=>setShowAdd(true)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>+ Add</button>
        </div>
      </div>
      <div style={{padding:"20px"}}>
        {showAdd&&(
          <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:14,padding:18,marginBottom:20}}>
            <div style={{fontSize:12,color:"#0cc0df",fontWeight:700,marginBottom:14}}>Add Employment</div>
            {[
              {k:"company",l:"Company Name *",p:"Acme Corp Pvt Ltd"},
              {k:"designation",l:"Designation *",p:"Software Engineer"},
              {k:"joiningDate",l:"Joining Date",p:"2020-01-01",t:"date"},
              {k:"relievingDate",l:"Relieving Date",p:"2022-12-31",t:"date"},
            ].map(fi=>(
              <div key={fi.k} style={{marginBottom:12}}>
                <Label>{fi.l}</Label>
                <Inp type={fi.t||"text"} value={form[fi.k]} onChange={e=>f(fi.k,e.target.value)} placeholder={fi.p}/>
              </div>
            ))}
            <div style={{marginBottom:12}}>
              <Label>Employment Type</Label>
              <div style={{display:"flex",gap:8}}>
                {["Full-time","Part-time","Contract","Freelance"].map(t=>(
                  <button key={t} onClick={()=>f("type",t)} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${form.type===t?"#ff914d":"rgba(255,255,255,0.1)"}`,background:form.type===t?"rgba(255,145,77,0.12)":"transparent",color:form.type===t?"#ff914d":"rgba(255,255,255,0.5)",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>{t}</button>
                ))}
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <BtnGhost onClick={()=>setShowAdd(false)}>Cancel</BtnGhost>
              <Btn onClick={addEmp} style={{flex:1,justifyContent:"center"}}>{saving?<Spin/>:"Save"}</Btn>
            </div>
          </div>
        )}
        {(profile.employment||[]).length===0&&!showAdd&&(
          <div style={{textAlign:"center",padding:"40px 20px",color:"rgba(255,255,255,0.3)"}}>
            <div style={{fontSize:40,marginBottom:12}}>💼</div>
            <div style={{fontSize:14,marginBottom:8}}>No employment added yet</div>
            <button onClick={()=>setShowAdd(true)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF}}>+ Add Employment</button>
          </div>
        )}
        {[...(profile.employment||[])].reverse().map((e,i)=>(
          <div key={i} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:16,marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:"#fff",marginBottom:2}}>{e.designation}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginBottom:4}}>{e.company}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{e.joiningDate} → {e.relievingDate||"Present"}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginTop:2}}>{e.type}</div>
              </div>
              <div style={{fontSize:10,padding:"3px 8px",borderRadius:99,background:"rgba(255,145,77,0.12)",border:"1px solid rgba(255,145,77,0.3)",color:"#ff914d",fontWeight:600}}>Unverified</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Card Screen ───────────────────────────────────────────────
function CardScreen({profile, setScreen}) {
  const ms = calcEmployeeScore(profile);
  const [tab,setTab]=useState("digital");

  const downloadCard=()=>{
    alert("Download feature: Use browser print → Save as PDF\n\nOr screenshot this card to save.");
  };

  const shareCard=()=>{
    const url = `https://meitho.com/p/${profile.meithoId}`;
    if(navigator.share){
      navigator.share({title:"My Meitho Profile",text:`Check my verified Meitho Profile - Score: ${ms.score}/1000`,url});
    } else {
      navigator.clipboard?.writeText(url);
      alert("Profile link copied: "+url);
    }
  };

  return(
    <div style={{minHeight:"100vh",background:"#0a0a1a",fontFamily:FF,paddingBottom:80}}>
      <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",padding:"48px 20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
          <button onClick={()=>setScreen("dashboard")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontSize:20,padding:0}}>←</button>
          <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"#fff"}}>Meitho Card</div>
          <div style={{width:32}}/>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:0,margin:"16px 20px",background:"rgba(255,255,255,0.04)",borderRadius:10,padding:4,border:"1px solid rgba(255,255,255,0.08)"}}>
        {[{id:"digital",l:"Digital Card"},{id:"physical",l:"Physical Card ₹199"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:"8px",borderRadius:8,border:"none",background:tab===t.id?"linear-gradient(90deg,#ff3131,#ff914d)":"transparent",color:tab===t.id?"#fff":"rgba(255,255,255,0.5)",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>
            {t.l}
          </button>
        ))}
      </div>

      {tab==="digital"&&(
        <div style={{padding:"0 20px"}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:20}}>
            <DigitalCard profile={profile}/>
          </div>
          <div style={{display:"flex",gap:10}}>
            <Btn onClick={downloadCard} style={{flex:1,justifyContent:"center"}}>⬇ Download</Btn>
            <Btn onClick={shareCard} style={{flex:1,justifyContent:"center",background:"rgba(255,255,255,0.08)"}}>🔗 Share</Btn>
          </div>
          <div style={{marginTop:14,background:"rgba(255,255,255,0.03)",borderRadius:10,padding:"12px 14px",border:"1px solid rgba(255,255,255,0.06)"}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:4}}>Your Public Profile Link</div>
            <div style={{fontSize:12,color:"#0cc0df",fontFamily:FFM}}>meitho.com/p/{profile.meithoId}</div>
          </div>
        </div>
      )}

      {tab==="physical"&&(
        <div style={{padding:"0 20px"}}>
          <div style={{background:"rgba(255,255,255,0.04)",borderRadius:14,padding:20,border:"1px solid rgba(255,255,255,0.08)",marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:8}}>Physical Meitho Card</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.7,marginBottom:16}}>
              Get a premium physical card with your Meitho Score, QR code, and verified badge. Perfect for interviews and networking events.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {["Premium PVC Card","QR Code Printed","Your Photo","Meitho Score","Hologram Badge","Free Delivery"].map(f=>(
                <div key={f} style={{fontSize:11,color:"rgba(255,255,255,0.6)",display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{color:"#10b981"}}>✓</span>{f}
                </div>
              ))}
            </div>
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{fontFamily:FFD,fontSize:32,fontWeight:900,color:"#ff914d"}}>₹199</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>Free delivery · 5-7 business days</div>
            </div>
            <div style={{marginBottom:12}}>
              <Label>Delivery Address</Label>
              <Inp value="" onChange={()=>{}} placeholder="Full address with PIN code"/>
            </div>
            <div style={{marginBottom:12}}>
              <Label>Phone for Delivery</Label>
              <Inp value={profile.mobile||""} onChange={()=>{}} placeholder="Mobile number"/>
            </div>
            <Btn onClick={()=>alert("Razorpay payment coming soon!")} style={{width:"100%",justifyContent:"center"}}>Order Now — ₹199 →</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Verification Screen ───────────────────────────────────────
function VerificationScreen({profile, setProfile, setScreen}) {
  const [step,setStep]=useState(profile.paymentStatus==="paid"?"status":"info");

  const timeline=[
    {label:"Submitted",done:profile.paymentStatus==="paid"},
    {label:"Under Review",done:profile.verificationStatus==="reviewing"},
    {label:"Documents Verified",done:false},
    {label:"Employment Verified",done:false},
    {label:"Education Verified",done:false},
    {label:"Completed",done:profile.verificationStatus==="completed"},
  ];

  return(
    <div style={{minHeight:"100vh",background:"#0a0a1a",fontFamily:FF,paddingBottom:80}}>
      <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",padding:"48px 20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <button onClick={()=>setScreen("dashboard")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontSize:20,padding:0}}>←</button>
          <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"#fff"}}>Verification</div>
          <div style={{width:32}}/>
        </div>
      </div>

      <div style={{padding:"20px"}}>
        {profile.paymentStatus!=="paid"?(
          <div>
            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{fontSize:40,marginBottom:12}}>🛡️</div>
              <div style={{fontFamily:FFD,fontSize:20,fontWeight:700,color:"#fff",marginBottom:8}}>Get Verified</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.7}}>
                Boost your Meitho Score with verified credentials
              </div>
            </div>
            <div style={{background:"rgba(255,255,255,0.04)",borderRadius:14,padding:18,marginBottom:16,border:"1px solid rgba(255,255,255,0.08)"}}>
              <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.6)",marginBottom:12}}>Verification Includes</div>
              {[
                {icon:"🪪",label:"Identity Verification",pts:"+50 pts"},
                {icon:"💼",label:"Employment Verification",pts:"+100 pts"},
                {icon:"🎓",label:"Education Verification",pts:"+80 pts"},
                {icon:"📄",label:"Document Authentication",pts:"+40 pts"},
              ].map(v=>(
                <div key={v.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <span style={{fontSize:18}}>{v.icon}</span>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.7)"}}>{v.label}</div>
                  </div>
                  <div style={{fontSize:11,color:"#10b981",fontWeight:700}}>{v.pts}</div>
                </div>
              ))}
            </div>
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{fontFamily:FFD,fontSize:36,fontWeight:900,color:"#ff914d"}}>₹50</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>One-time verification fee</div>
            </div>
            <Btn onClick={()=>alert("Razorpay integration coming soon!")} style={{width:"100%",justifyContent:"center",marginBottom:12}}>
              Proceed to Payment ₹50 →
            </Btn>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",textAlign:"center"}}>🔒 Secure payment via Razorpay</div>
          </div>
        ):(
          <div>
            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{fontSize:40,marginBottom:8}}>✅</div>
              <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"#10b981"}}>Verification In Progress</div>
            </div>
            <div style={{background:"rgba(255,255,255,0.04)",borderRadius:14,padding:18,border:"1px solid rgba(255,255,255,0.08)"}}>
              {timeline.map((t,i)=>(
                <div key={i} style={{display:"flex",gap:14,alignItems:"flex-start",marginBottom:i<timeline.length-1?16:0}}>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:t.done?"rgba(16,185,129,0.2)":"rgba(255,255,255,0.06)",border:`2px solid ${t.done?"#10b981":"rgba(255,255,255,0.1)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {t.done?<span style={{color:"#10b981",fontSize:12}}>✓</span>:<span style={{width:8,height:8,borderRadius:"50%",background:"rgba(255,255,255,0.1)",display:"block"}}/>}
                    </div>
                    {i<timeline.length-1&&<div style={{width:2,height:24,background:"rgba(255,255,255,0.06)",margin:"4px 0"}}/>}
                  </div>
                  <div style={{paddingTop:4}}>
                    <div style={{fontSize:13,color:t.done?"#10b981":"rgba(255,255,255,0.5)",fontWeight:t.done?700:400}}>{t.label}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{marginTop:14,fontSize:11,color:"rgba(255,255,255,0.3)",textAlign:"center"}}>Estimated completion: 2-3 business days</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Score Screen ──────────────────────────────────────────────
function ScoreScreen({profile, setScreen}) {
  const ms = calcEmployeeScore(profile);
  return(
    <div style={{minHeight:"100vh",background:"#0a0a1a",fontFamily:FF,paddingBottom:80}}>
      <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",padding:"48px 20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <button onClick={()=>setScreen("dashboard")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontSize:20,padding:0}}>←</button>
          <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"#fff"}}>Meitho Score</div>
          <div style={{width:32}}/>
        </div>
        <div style={{display:"flex",justifyContent:"center"}}>
          <ScoreRing score={ms.score} color={ms.color} size={160}/>
        </div>
        <div style={{textAlign:"center",marginTop:16}}>
          <div style={{fontFamily:FFD,fontSize:24,fontWeight:800,color:ms.color}}>{ms.grade}</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:4}}>Out of 1000 points</div>
        </div>
      </div>

      <div style={{padding:"20px"}}>
        {/* Breakdown */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Score Breakdown</div>
          {[
            {label:"Identity Verification",key:"identity",max:200,icon:"🪪"},
            {label:"Employment History",key:"employment",max:300,icon:"💼"},
            {label:"Education",key:"education",max:200,icon:"🎓"},
            {label:"Skills & Certifications",key:"skills",max:150,icon:"⭐"},
            {label:"Profile Completeness",key:"profileComplete",max:150,icon:"👤"},
          ].map(s=>(
            <div key={s.key} style={{background:"rgba(255,255,255,0.03)",borderRadius:12,padding:"14px 16px",marginBottom:10,border:"1px solid rgba(255,255,255,0.06)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:16}}>{s.icon}</span>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",fontWeight:500}}>{s.label}</div>
                </div>
                <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:ms.color}}>{ms.breakdown[s.key]}<span style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>/{s.max}</span></div>
              </div>
              <div style={{height:5,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${(ms.breakdown[s.key]/s.max)*100}%`,background:ms.color,borderRadius:3}}/>
              </div>
            </div>
          ))}
        </div>

        {/* Tips */}
        <div style={{background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.15)",borderRadius:12,padding:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#0cc0df",marginBottom:10}}>💡 How to improve your score</div>
          {[
            !profile.uan&&"Add your UAN number (+50 pts)",
            !(profile.education?.length>0)&&"Add education details (+80 pts)",
            !(profile.employment?.length>0)&&"Add employment history (+100 pts)",
            !(profile.certifications?.length>0)&&"Add certifications (+30 pts each)",
            profile.paymentStatus!=="paid"&&"Get verified (+200 pts)",
          ].filter(Boolean).map((t,i)=>(
            <div key={i} style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:6,display:"flex",gap:6}}>
              <span style={{color:"#ff914d"}}>→</span>{t}
            </div>
          ))}
        </div>

        {/* Badges */}
        {ms.badges?.length>0&&(
          <div style={{marginTop:20}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:12}}>Your Badges</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {ms.badges.map((b,i)=>(
                <div key={i} style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:99,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.7)"}}>{b}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settings Screen ───────────────────────────────────────────
function SettingsScreen({profile, setScreen, onLogout}) {
  return(
    <div style={{minHeight:"100vh",background:"#0a0a1a",fontFamily:FF,paddingBottom:80}}>
      <div style={{background:"linear-gradient(135deg,#0a0a1a,#1a1a2e)",padding:"48px 20px 24px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <button onClick={()=>setScreen("dashboard")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontSize:20,padding:0}}>←</button>
          <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"#fff"}}>Settings</div>
          <div style={{width:32}}/>
        </div>
      </div>
      <div style={{padding:"20px"}}>
        {[
          {icon:"👤",label:"Edit Profile",action:()=>setScreen("profile")},
          {icon:"🏆",label:"My Meitho Card",action:()=>setScreen("card")},
          {icon:"✅",label:"Verification Status",action:()=>setScreen("verification")},
          {icon:"📊",label:"My Score",action:()=>setScreen("score")},
          {icon:"🔐",label:"Change Password",action:()=>alert("Coming soon")},
          {icon:"🔔",label:"Notifications",action:()=>alert("Coming soon")},
          {icon:"❓",label:"Help & Support",action:()=>window.open("mailto:help@meitho.com")},
        ].map((s,i)=>(
          <button key={i} onClick={s.action} style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"14px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,marginBottom:10,fontFamily:FF,textAlign:"left"}}>
            <span style={{fontSize:20}}>{s.icon}</span>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",fontWeight:500,flex:1}}>{s.label}</div>
            <span style={{color:"rgba(255,255,255,0.3)",fontSize:16}}>›</span>
          </button>
        ))}
        <button onClick={onLogout} style={{width:"100%",background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:12,padding:"14px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,marginTop:10,fontFamily:FF}}>
          <span style={{fontSize:20}}>🚪</span>
          <div style={{fontSize:13,color:"#ef4444",fontWeight:600,flex:1}}>Sign Out</div>
        </button>
      </div>
    </div>
  );
}

// ── Bottom Navigation ─────────────────────────────────────────
function BottomNav({screen, setScreen}) {
  const tabs=[
    {id:"dashboard",icon:"🏠",label:"Home"},
    {id:"score",icon:"🏆",label:"Score"},
    {id:"card",icon:"💳",label:"Card"},
    {id:"profile",icon:"👤",label:"Profile"},
    {id:"settings",icon:"⚙️",label:"Settings"},
  ];
  return(
    <div style={{position:"fixed",bottom:0,left:0,right:0,background:"rgba(10,10,26,0.95)",backdropFilter:"blur(20px)",borderTop:"1px solid rgba(255,255,255,0.08)",display:"flex",zIndex:1000}}>
      {tabs.map(t=>(
        <button key={t.id} onClick={()=>setScreen(t.id)} style={{flex:1,padding:"10px 0 6px",border:"none",background:"transparent",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
          <span style={{fontSize:20,filter:screen===t.id?"none":"grayscale(1)",opacity:screen===t.id?1:0.4}}>{t.icon}</span>
          <span style={{fontSize:9,color:screen===t.id?"#ff914d":"rgba(255,255,255,0.3)",fontWeight:screen===t.id?700:400,fontFamily:FF}}>{t.label}</span>
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN EMPLOYEE APP
// ═══════════════════════════════════════════════════════════════
export default function EmployeeApp() {
  const [authUser,setAuthUser]=useState(null);
  const [profile,setProfile]=useState(null);
  const [screen,setScreen]=useState("splash");
  const [authMode,setAuthMode]=useState("login");
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    // Splash screen
    const t=setTimeout(()=>{
      if(!authUser)setScreen("auth");
    },2000);

    const unsub=onAuthStateChanged(empAuth,async(user)=>{
      if(user){
        try{
          const snap=await getDoc(doc(empDb,"employeeProfiles",user.uid));
          if(snap.exists()){
            setProfile({...snap.data(),uid:user.uid});
            setScreen("dashboard");
          }
        }catch(e){console.warn("Profile load:",e);}
      } else {
        setProfile(null);
        setScreen("auth");
      }
      setAuthUser(user);
      setLoading(false);
    });

    return()=>{unsub();clearTimeout(t);};
  },[]);

  const handleLogout=async()=>{
    await signOut(empAuth);
    setScreen("auth");
    setAuthMode("login");
  };

  if(screen==="splash") return <SplashScreen/>;

  if(screen==="auth") return authMode==="login"
    ? <LoginScreen onLogin={()=>{}} onRegister={()=>setAuthMode("register")}/>
    : <RegisterScreen onLogin={()=>setAuthMode("login")}/>;

  if(!profile) return <SplashScreen/>;

  const renderScreen=()=>{
    switch(screen){
      case "dashboard": return <DashboardScreen profile={profile} setScreen={setScreen}/>;
      case "profile": return <ProfileScreen profile={profile} setProfile={setProfile} setScreen={setScreen}/>;
      case "education": return <EducationScreen profile={profile} setProfile={setProfile} setScreen={setScreen}/>;
      case "employment": return <EmploymentScreen profile={profile} setProfile={setProfile} setScreen={setScreen}/>;
      case "card": return <CardScreen profile={profile} setScreen={setScreen}/>;
      case "verification": return <VerificationScreen profile={profile} setProfile={setProfile} setScreen={setScreen}/>;
      case "score": return <ScoreScreen profile={profile} setScreen={setScreen}/>;
      case "settings": return <SettingsScreen profile={profile} setScreen={setScreen} onLogout={handleLogout}/>;
      default: return <DashboardScreen profile={profile} setScreen={setScreen}/>;
    }
  };

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#060610,#0d0d1f)",display:"flex",alignItems:"flex-start",justifyContent:"center"}}>
      <div style={{width:"100%",maxWidth:520,minHeight:"100vh",position:"relative",background:"#0a0a1a",boxShadow:"0 0 80px rgba(0,0,0,0.8)"}}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        input { -webkit-appearance: none; }
        ::-webkit-scrollbar { display: none; }
      `}</style>
      {renderScreen()}
      {!["splash","auth"].includes(screen)&&<BottomNav screen={screen} setScreen={setScreen}/>}
      </div>
    </div>
  );
}
